import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import '../styles/UserDashboard.css';

const UserDashboard = ({ onAddToCart }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'orders';
  const { user, updateProfile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [savedOutfits, setSavedOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [profile, setProfile] = useState({
    username: user?.username || '',
    email: user?.email || '',
    password: '',
    confirmPassword: '',
  });

  const setActiveTab = tab => {
    setSearchParams({ tab });
  };

  useEffect(() => {
    setProfile(current => ({
      ...current,
      username: user?.username || '',
      email: user?.email || '',
    }));
  }, [user]);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      try {
        if (activeTab === 'orders') {
          const ordersResponse = await fetch('/api/orders', { headers });
          setOrders(ordersResponse.ok ? await ordersResponse.json() : []);
        } else if (activeTab === 'outfits') {
          const outfitsResponse = await fetch('/api/custom-outfits', { headers });
          setSavedOutfits(outfitsResponse.ok ? await outfitsResponse.json() : []);
        }
      } catch (error) {
        setMessage('Could not load your account data.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [activeTab]);

  const saveProfile = async event => {
    event.preventDefault();
    setMessage('');
    if (profile.password && profile.password !== profile.confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    try {
      const payload = { username: profile.username, email: profile.email };
      if (profile.password) {
        payload.password = profile.password;
      }
      await updateProfile(payload);
      setProfile(current => ({ ...current, password: '', confirmPassword: '' }));
      setMessage('Account updated.');
    } catch (error) {
      setMessage(error.userMessage || 'Could not update your account.');
    }
  };

  const deleteOutfit = async outfitId => {
    const response = await fetch(`/api/custom-outfits/${outfitId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (response.ok) {
      setSavedOutfits(current => current.filter(outfit => outfit.id !== outfitId));
    }
  };

  const addOutfitToCart = outfit => {
    onAddToCart?.(
      outfit.items.map(item => ({
        id: item.product.id,
        name: item.product.name,
        price: item.product.price,
        image_url: item.product.image_url,
        image: item.product.image_url,
        size: item.size,
        color: item.color,
        quantity: 1,
      })),
    );
  };

  return (
    <div className='dashboard-container'>
      <h1 className='dashboard-title'>My Account</h1>
      {message && <p>{message}</p>}

      <div className='dashboard-content'>
        <div className='dashboard-sidebar'>
          <div className='user-welcome'>
            <h3>Welcome, {user?.username || 'Fashion Enthusiast'}!</h3>
            <p>{user?.email}</p>
          </div>

          <div className='dashboard-nav'>
            {['orders', 'outfits', 'account'].map(tab => (
              <button
                key={tab}
                className={`dashboard-nav-item ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'orders' ? 'My Orders' : tab === 'outfits' ? 'Saved Outfits' : 'Account Settings'}
              </button>
            ))}
          </div>
        </div>

        <div className='dashboard-main'>
          {loading && activeTab !== 'account' ? (
            <LoadingSpinner />
          ) : (
            <>
              {activeTab === 'orders' && (
                <div className='orders-section'>
                  <h2>My Orders</h2>
                  {orders.length > 0 ? (
                    <div className='orders-list'>
                      {orders.map(order => (
                        <div key={order.id} className='order-card'>
                          <div className='order-header'>
                            <h3>Order {order.order_number}</h3>
                            <span className='order-date'>
                              {order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}
                            </span>
                          </div>
                          <div className='order-info'>
                            <p>Total: ${Number(order.total_price).toFixed(2)}</p>
                            <p>
                              Status: <span className='status-badge'>{order.status}</span>
                            </p>
                          </div>
                          <ul>
                            {(order.items || []).map(item => (
                              <li key={item.id}>
                                {item.name} × {item.quantity}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='empty-state'>
                      <p>You haven&apos;t placed any orders yet.</p>
                      <Link to='/shop' className='shop-now-btn'>
                        Shop Now
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'outfits' && (
                <div className='outfits-section'>
                  <h2>Saved Outfits</h2>
                  {savedOutfits.length > 0 ? (
                    <div className='outfits-grid'>
                      {savedOutfits.map(outfit => (
                        <div key={outfit.id} className='outfit-card'>
                          <h3>{outfit.name}</h3>
                          <div className='outfit-items'>
                            {outfit.items.map(item => (
                              <div key={item.id} className='outfit-item'>
                                <img src={item.product.image_url} alt={item.product.name} />
                                <div className='item-details'>
                                  <p>{item.product.name}</p>
                                  <p>Size: {item.size}</p>
                                  <p>Color: {item.color}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className='outfit-actions'>
                            <button className='add-to-cart-btn' onClick={() => addOutfitToCart(outfit)}>
                              Add to Cart
                            </button>
                            <button className='delete-outfit-btn' onClick={() => deleteOutfit(outfit.id)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='empty-state'>
                      <p>You haven&apos;t saved any custom outfits yet.</p>
                      <Link to='/customize-outfit' className='create-outfit-btn'>
                        Create an Outfit
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'account' && (
                <div className='account-section'>
                  <h2>Account Settings</h2>
                  <form className='account-form' onSubmit={saveProfile}>
                    <div className='form-group'>
                      <label htmlFor='username'>Username</label>
                      <input
                        id='username'
                        type='text'
                        value={profile.username}
                        onChange={event => setProfile(current => ({ ...current, username: event.target.value }))}
                      />
                    </div>
                    <div className='form-group'>
                      <label htmlFor='email'>Email</label>
                      <input
                        id='email'
                        type='email'
                        value={profile.email}
                        onChange={event => setProfile(current => ({ ...current, email: event.target.value }))}
                      />
                    </div>
                    <div className='form-group'>
                      <label htmlFor='password'>New Password</label>
                      <input
                        id='password'
                        type='password'
                        placeholder='Leave blank to keep current password'
                        value={profile.password}
                        onChange={event => setProfile(current => ({ ...current, password: event.target.value }))}
                      />
                    </div>
                    <div className='form-group'>
                      <label htmlFor='confirmPassword'>Confirm New Password</label>
                      <input
                        id='confirmPassword'
                        type='password'
                        placeholder='Leave blank to keep current password'
                        value={profile.confirmPassword}
                        onChange={event =>
                          setProfile(current => ({ ...current, confirmPassword: event.target.value }))
                        }
                      />
                    </div>
                    <button type='submit' className='save-btn'>
                      Save Changes
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
